import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useTechSession from '../../hooks/useTechSession';
import useLanguage from '../../hooks/useLanguage';
import useVoice from '../../hooks/useVoice';
import TechButton from '../../components/tech/TechButton';
import VoiceButton from '../../components/tech/VoiceButton';

/**
 * Post-fix gap-analysis interview — voice-first interaction.
 *
 * Purpose: understand the gap between what the system predicted
 * and what the technician actually found.
 *
 * Flow:
 * - Max 3 questions, read aloud automatically
 * - Answer options are also read aloud
 * - Free-text answers via voice (STT) or typing
 * - "Done" returns to task list (screen 3)
 */

// Hebrew-friendly names for error codes
const ERROR_NAMES_HE = {
  DRIVE_PEAK_LOAD_HALT: 'עצירת עומס שיא מוטור נסיעה',
  DRIVE_STALL_HALT: 'עצירת תקיעה מוטור נסיעה',
  DRIVE_HIGH_LOAD_HALT: 'עצירת עומס גבוה מוטור נסיעה',
  DRIVE_PEAK_LOAD_GO_HOME: 'חזרה הביתה עקב עומס שיא',
  DRIVE_STALL_GO_HOME: 'חזרה הביתה עקב תקיעה',
  DRIVE_MOTOR_NOT_ROTATING: 'מוטור נסיעה לא מסתובב',
  CLEAN_STALL_HALT: 'עצירת תקיעה מוטור ניקוי',
  CLEAN_MOTOR_ERROR_STAY_HOME: 'שגיאת מוטור ניקוי',
  CLEAN_MOTOR_NOT_ROTATING: 'מוטור ניקוי לא מסתובב',
  CLEAN_OVERCURRENT_HALT: 'עצירת זרם יתר מוטור ניקוי',
  BATTERY_VOLTAGE_LOW: 'מתח סוללה נמוך',
  BATTERY_VOLTAGE_CRITICAL: 'מתח סוללה קריטי',
  PCB_COMMUNICATION_ERROR: 'שגיאת תקשורת כרטיס',
};

// Build gap-analysis questions based on task data
function buildGapQuestions(task, isHe) {
  const questions = [];
  const rawError = task?.primary_error || '';
  const predicted = isHe
    ? (ERROR_NAMES_HE[rawError] || rawError.replace(/_/g, ' '))
    : rawError.replace(/_/g, ' ');

  // Q1: Did the prediction match?
  questions.push({
    key: 'prediction_match',
    text: isHe
      ? `המערכת חשדה ב: ${predicted}. האם זה מה שמצאת?`
      : `The system suspected: ${predicted}. Is that what you found?`,
    options: isHe
      ? ['כן, בדיוק', 'חלקית', 'לא, משהו אחר']
      : ['Yes, exactly', 'Partially', 'No, something else'],
    followUp: 'actual_finding',
  });

  // Q2: What did you actually find? (only if mismatch or partial)
  questions.push({
    key: 'actual_finding',
    text: isHe
      ? 'מה מצאת בפועל? תאר בקצרה.'
      : 'What did you actually find? Describe briefly.',
    options: null, // Free text / voice
    followUp: null,
  });

  // Q3: Was there anything unexpected?
  questions.push({
    key: 'unexpected',
    text: isHe
      ? 'האם היה משהו לא צפוי שכדאי שהמערכת תדע?'
      : 'Was there anything unexpected the system should know about?',
    options: isHe
      ? ['לא, הכל כצפוי', 'כן, יש עוד בעיה', 'הרובוט זקוק לביקור נוסף']
      : ['No, all as expected', 'Yes, there\'s another issue', 'Robot needs another visit'],
    followUp: null,
  });

  return questions;
}


export default function PostFixInterview() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { session } = useTechSession();
  const { language, t, isRTL } = useLanguage();
  const { speak, speaking, stopSpeaking, listen, listening, transcript } = useVoice();

  const isHe = language.startsWith('he');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [freeText, setFreeText] = useState('');
  const autoReadRef = useRef(-1);

  // Build questions from task context (we get task info from session/localStorage)
  const taskInfo = session ? {
    primary_error: new URLSearchParams(window.location.search).get('error') || '',
  } : {};
  const questions = buildGapQuestions(taskInfo, isHe);
  const question = questions[currentQ];

  // Determine how many questions to actually show
  // If Q1 answer is "Yes, exactly" → skip Q2 (actual_finding), go to Q3
  const shouldSkipQ2 = () => {
    const q1Answer = answers['prediction_match'];
    if (!q1Answer) return false;
    return q1Answer === 'כן, בדיוק' || q1Answer === 'Yes, exactly';
  };

  // Auto-read the question when it changes
  useEffect(() => {
    if (question && autoReadRef.current !== currentQ) {
      autoReadRef.current = currentQ;
      const langCode = language;
      // Read the question
      let fullText = question.text;
      // Also read the options
      if (question.options?.length > 0) {
        const optionsText = question.options.join(isHe ? ', ' : ', ');
        fullText += isHe ? ` האפשרויות: ${optionsText}` : ` Options: ${optionsText}`;
      }
      setTimeout(() => speak(fullText, langCode, 0.9), 300);
    }
    // eslint-disable-next-line
  }, [currentQ, question]);

  const handleSelectOption = (opt) => {
    // Read the selected option
    speak(opt, language, 1.0);
    setAnswers(prev => ({ ...prev, [question.key]: opt }));
  };

  const handleFreeTextDone = () => {
    if (freeText.trim() || transcript.trim()) {
      const answer = freeText.trim() || transcript.trim();
      setAnswers(prev => ({ ...prev, [question.key]: answer }));
      setFreeText('');
    }
  };

  const handleNext = () => {
    // Save free-text answer if pending
    if (!question.options && (freeText.trim() || transcript.trim())) {
      handleFreeTextDone();
    }

    stopSpeaking();

    let nextQ = currentQ + 1;

    // Skip Q2 if prediction matched exactly
    if (nextQ === 1 && shouldSkipQ2()) {
      nextQ = 2;
    }

    if (nextQ >= questions.length) {
      // Done — go back to task list
      // TODO: send answers to backend
      navigate('/tech/tasks');
    } else {
      setCurrentQ(nextQ);
      setFreeText('');
    }
  };

  const hasAnswer = answers[question?.key] || freeText.trim() || transcript.trim();

  if (!session) { navigate('/tech'); return null; }

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#e6edf3' }}>
          {isRTL ? 'ניתוח פערים' : 'Gap Analysis'}
        </h2>
        <div style={{ fontSize: 13, color: '#8b949e' }}>
          {isRTL ? `שאלה ${currentQ + 1} מתוך ${questions.length}` : `Question ${currentQ + 1} of ${questions.length}`}
        </div>
      </div>

      {/* Progress bar */}
      <div style={styles.progressBar}>
        <div style={{
          ...styles.progressFill,
          width: `${((currentQ + 1) / questions.length) * 100}%`,
        }} />
      </div>

      {question && (
        <div style={styles.questionCard}>
          {/* Question text + replay button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.5, flex: 1, color: '#e6edf3' }}>
              {question.text}
            </div>
            <VoiceButton
              mode="speak"
              active={speaking}
              size={40}
              onClick={() => {
                if (speaking) { stopSpeaking(); return; }
                let fullText = question.text;
                if (question.options?.length > 0) {
                  fullText += isHe ? ` האפשרויות: ${question.options.join(', ')}` : ` Options: ${question.options.join(', ')}`;
                }
                speak(fullText, language, 0.9);
              }}
            />
          </div>

          {/* Answer options (buttons — read aloud on tap) */}
          {question.options?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
              {question.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => handleSelectOption(opt)}
                  style={{
                    ...styles.optionBtn,
                    ...(answers[question.key] === opt ? styles.optionBtnActive : {}),
                  }}
                >
                  <span style={styles.optionNum}>{i + 1}</span>
                  <span style={{ fontSize: 15, flex: 1 }}>{opt}</span>
                  {answers[question.key] === opt && (
                    <span style={{ fontSize: 18 }}>✓</span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            /* Free-text with voice input */
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 8 }}>
                {isRTL ? 'לחץ על המיקרופון לדבר, או הקלד:' : 'Tap the mic to speak, or type:'}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                <textarea
                  value={freeText || answers[question.key] || ''}
                  onChange={e => setFreeText(e.target.value)}
                  placeholder={isRTL ? 'דבר או הקלד כאן...' : 'Speak or type here...'}
                  style={styles.textarea}
                  rows={3}
                />
                <VoiceButton
                  mode="listen"
                  active={listening}
                  size={48}
                  onClick={() => {
                    if (listening) return;
                    listen(language, (text) => {
                      setFreeText(text);
                      setAnswers(prev => ({ ...prev, [question.key]: text }));
                    });
                  }}
                />
              </div>
              {transcript && !freeText && (
                <div style={{ fontSize: 12, color: '#6c5ce7', marginTop: 6 }}>
                  {isRTL ? 'זוהה: ' : 'Detected: '}{transcript}
                </div>
              )}
            </div>
          )}

          {/* Next / Finish button */}
          <TechButton
            fullWidth
            onClick={handleNext}
            disabled={!hasAnswer}
            style={{ marginTop: 20 }}
          >
            {currentQ >= questions.length - 1 || (currentQ === 0 && shouldSkipQ2() && currentQ + 2 >= questions.length)
              ? (isRTL ? '✓ סיום וחזרה למשימות' : '✓ Finish & Back to Tasks')
              : (isRTL ? 'הבא →' : 'Next →')
            }
          </TechButton>

          {/* Skip all button */}
          <TechButton
            fullWidth
            variant="ghost"
            onClick={() => navigate('/tech/tasks')}
            style={{ marginTop: 8 }}
          >
            {isRTL ? 'דלג וחזור למשימות' : 'Skip & Back to Tasks'}
          </TechButton>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: {
    marginBottom: 12,
  },
  progressBar: {
    height: 4,
    background: '#30363d',
    borderRadius: 2,
    marginBottom: 20,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: '#6c5ce7',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  questionCard: {
    background: '#161b22',
    borderRadius: 16,
    padding: '24px 20px',
  },
  optionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 18px',
    background: '#0d1117',
    border: '2px solid #30363d',
    borderRadius: 12,
    color: '#e6edf3',
    fontSize: 15,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    minHeight: 52,
    transition: 'border-color 0.2s, background 0.2s',
  },
  optionBtnActive: {
    borderColor: '#6c5ce7',
    background: '#6c5ce715',
  },
  optionNum: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: '#30363d',
    color: '#8b949e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  textarea: {
    flex: 1,
    padding: '12px 14px',
    fontSize: 15,
    background: '#0d1117',
    border: '2px solid #30363d',
    borderRadius: 10,
    color: '#e6edf3',
    resize: 'vertical',
    fontFamily: 'inherit',
    lineHeight: 1.4,
  },
};
